import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApi, errMsg } from "@/lib/api";
import { toast } from "sonner";

const PLANS = [
  { key: "single_download", name: "Single Download", price: "$6.99", note: "One-time, incl. GST", perks: ["This document, watermark-free", "Re-download it any time", "Keep editing after you pay"] },
  { key: "pass_30", name: "30-Day Pass", price: "$19.99", note: "One-time, no subscription, incl. GST", perks: ["Unlimited clean downloads for 30 days", "All resumes and cover letters", "Best for applying to many jobs"], best: true },
];

export const PricingDialog = ({ open, onOpenChange, docId, returnPath, beforeCheckout }) => {
  const api = useApi();
  const [loading, setLoading] = useState(null);

  const buy = async (key) => {
    setLoading(key);
    try {
      if (beforeCheckout) await beforeCheckout();
      localStorage.setItem("pp_return", returnPath);
      const { data } = await api.post("/payments/checkout", { lookup_key: key, document_id: docId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e, "Could not start checkout"));
      setLoading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-paper-border bg-paper p-0" data-testid="pricing-dialog">
        <div className="p-6 sm:p-8">
          <DialogHeader>
            <p className="eyebrow">Download your PDF</p>
            <DialogTitle className="font-serif text-3xl font-medium text-ink">Ready to send it out?</DialogTitle>
            <DialogDescription className="text-ink-soft">Pay once, download a clean PDF with no watermark. Prices in Australian dollars, GST included. Secure checkout by Stripe. By paying you agree to our <a href="/terms" target="_blank" rel="noreferrer" className="text-thread underline-offset-2 hover:underline" data-testid="pricing-terms-link">terms of use and refund policy</a>.</DialogDescription>
          </DialogHeader>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {PLANS.map((p) => (
              <div key={p.key} className={`relative flex flex-col rounded-xl border bg-white p-5 ${p.best ? "border-thread shadow-md" : "border-paper-border"}`}>
                {p.best && <span className="absolute -top-2.5 left-5 rounded-full bg-thread px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-white">Best value</span>}
                <p className="font-semibold text-ink">{p.name}</p>
                <p className="mt-2 font-serif text-4xl text-ink" data-testid={`pricing-dialog-price-${p.key}`}>{p.price}<span className="ml-1.5 align-middle font-mono text-xs text-taupe">AUD</span></p>
                <p className="text-xs text-taupe">{p.note}</p>
                <ul className="mt-4 flex-1 space-y-2 text-sm text-ink-soft">
                  {p.perks.map((x) => <li key={x} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-thread" />{x}</li>)}
                </ul>
                <button className={`${p.best ? "btn-thread" : "btn-ink"} mt-5 w-full`} disabled={!!loading} onClick={() => buy(p.key)} data-testid={`checkout-${p.key}-btn`}>
                  {loading === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Pay {p.price} AUD
                </button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
